/**
 * The three shapes of one relation form — docs/08-frontend-and-ux.md §3.6.
 *
 * `kind` switches which discriminators are asked for, matching the kind-dependent
 * CHECK of docs/02 §2.2 exactly: asking for a `union_type` on a sibling row would
 * be a field the database will not store.
 *
 * The endpoints themselves are the caller's business — in the person editor one
 * of them is "the person being created", which has no id yet.
 */

import { useTranslation } from 'react-i18next';
import {
  PARENT_BASIS,
  PARENT_ROLE,
  RELATION_KIND,
  SIBLING_BASIS,
  UNION_END_REASON,
  UNION_TYPE,
  isFuzzyDateEmpty,
  type FuzzyDateParts,
  type ParentBasis,
  type ParentRole,
  type RelationKind,
  type SiblingBasis,
  type UnionEndReason,
  type UnionType,
} from '@famtree/shared';
import { FuzzyDateField } from '../../ui/FuzzyDateField.js';
import ui from '../../ui/components.module.css';
import styles from './people.module.css';

/** The editable half of a relation, before endpoints are attached. */
export interface RelationDraft {
  kind: RelationKind;
  parent_role: ParentRole | null;
  parent_basis: ParentBasis | null;
  union_type: UnionType | null;
  end_reason: UnionEndReason | null;
  sibling_basis: SiblingBasis | null;
  started: FuzzyDateParts | null;
  ended: FuzzyDateParts | null;
  note: string;
  source_note: string;
}

export function emptyRelationDraft(kind: RelationKind = 'parent_child'): RelationDraft {
  return {
    kind,
    parent_role: kind === 'parent_child' ? 'parent' : null,
    parent_basis: kind === 'parent_child' ? 'biological' : null,
    union_type: kind === 'union' ? 'marriage' : null,
    end_reason: null,
    sibling_basis: kind === 'sibling' ? 'full' : null,
    started: null,
    ended: null,
    note: '',
    source_note: '',
  };
}

/** Switching kind resets the other kinds' fields rather than smuggling them along. */
export function withKind(draft: RelationDraft, kind: RelationKind): RelationDraft {
  return { ...emptyRelationDraft(kind), note: draft.note, source_note: draft.source_note };
}

/** The wire shape: nulls where the kind forbids a value, dates dropped when empty. */
export function toRelationPayload(
  draft: RelationDraft,
  personAId: string | null,
  personBId: string | null,
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    kind: draft.kind,
    person_a_id: personAId,
    person_b_id: personBId,
    note: draft.note.trim() || null,
    source_note: draft.source_note.trim() || null,
  };

  if (draft.kind === 'parent_child') {
    base.parent_role = draft.parent_role;
    base.parent_basis = draft.parent_basis;
    base.started = isFuzzyDateEmpty(draft.started) ? null : draft.started;
    base.ended = isFuzzyDateEmpty(draft.ended) ? null : draft.ended;
  } else if (draft.kind === 'union') {
    base.union_type = draft.union_type;
    base.end_reason = draft.end_reason;
    base.started = isFuzzyDateEmpty(draft.started) ? null : draft.started;
    base.ended = isFuzzyDateEmpty(draft.ended) ? null : draft.ended;
  } else {
    // Sibling rows carry no dates at all (`ra_sibling_no_dates_check`).
    base.sibling_basis = draft.sibling_basis;
  }
  return base;
}

export interface RelationFieldsProps {
  draft: RelationDraft;
  onChange: (draft: RelationDraft) => void;
  /** Kind is fixed when the form was opened as "add a parent", say. */
  lockKind?: boolean;
}

export function RelationFields({ draft, onChange, lockKind }: RelationFieldsProps): JSX.Element {
  const { t } = useTranslation();
  const set = (patch: Partial<RelationDraft>): void => onChange({ ...draft, ...patch });

  // Time bounds only mean something for a parent edge that *started* at some
  // point — an adoption, a guardianship (docs/08 §3.6).
  const parentDated =
    draft.parent_basis === 'adoptive' ||
    draft.parent_basis === 'foster' ||
    draft.parent_basis === 'guardianship' ||
    draft.parent_basis === 'step';

  return (
    <>
      {!lockKind && (
        <fieldset className={ui.field}>
          <legend className={ui.label}>{t('relations.kind')}</legend>
          <div className={styles.inlineRow}>
            {RELATION_KIND.map((kind) => (
              <label key={kind}>
                <input
                  type="radio"
                  name="relation-kind"
                  value={kind}
                  checked={draft.kind === kind}
                  onChange={() => onChange(withKind(draft, kind))}
                />{' '}
                {t(`enums.relation_kind.${kind}`)}
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {draft.kind === 'parent_child' && (
        <>
          <div className={styles.inlineRow}>
            <label className={ui.field}>
              <span className={ui.label}>{t('relations.parentRole')}</span>
              <select
                className={ui.input}
                value={draft.parent_role ?? 'parent'}
                onChange={(event) => set({ parent_role: event.target.value as ParentRole })}
              >
                {PARENT_ROLE.map((role) => (
                  <option key={role} value={role}>
                    {t(`enums.parent_role.${role}`)}
                  </option>
                ))}
              </select>
            </label>

            <label className={ui.field}>
              <span className={ui.label}>{t('relations.parentBasis')}</span>
              <select
                className={ui.input}
                value={draft.parent_basis ?? 'biological'}
                onChange={(event) => set({ parent_basis: event.target.value as ParentBasis })}
              >
                {PARENT_BASIS.map((basis) => (
                  <option key={basis} value={basis}>
                    {t(`enums.parent_basis.${basis}`)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className={ui.muted}>
            {t(`relations.basisHelp.${draft.parent_basis ?? 'biological'}`)}
          </p>

          {parentDated && (
            <>
              <FuzzyDateField
                legend={t('relations.started')}
                value={draft.started}
                onChange={(started) => set({ started })}
              />
              <FuzzyDateField
                legend={t('relations.ended')}
                value={draft.ended}
                onChange={(ended) => set({ ended })}
              />
            </>
          )}
        </>
      )}

      {draft.kind === 'union' && (
        <>
          <label className={ui.field}>
            <span className={ui.label}>{t('relations.unionType')}</span>
            <select
              className={ui.input}
              value={draft.union_type ?? 'marriage'}
              onChange={(event) => set({ union_type: event.target.value as UnionType })}
            >
              {UNION_TYPE.map((type) => (
                <option key={type} value={type}>
                  {t(`enums.union_type.${type}`)}
                </option>
              ))}
            </select>
          </label>

          <FuzzyDateField
            legend={t('relations.started')}
            value={draft.started}
            onChange={(started) => set({ started })}
          />
          <FuzzyDateField
            legend={t('relations.ended')}
            value={draft.ended}
            onChange={(ended) => set({ ended })}
          />

          <label className={ui.field}>
            <span className={ui.label}>{t('relations.endReason')}</span>
            <select
              className={ui.input}
              value={draft.end_reason ?? ''}
              onChange={(event) =>
                set({ end_reason: (event.target.value || null) as UnionEndReason | null })
              }
            >
              <option value="">{t('relations.ongoing')}</option>
              {UNION_END_REASON.map((reason) => (
                <option key={reason} value={reason}>
                  {t(`enums.union_end_reason.${reason}`)}
                </option>
              ))}
            </select>
            {!isFuzzyDateEmpty(draft.ended) && draft.end_reason == null && (
              <span className={ui.fieldError}>{t('relations.endReasonRequired')}</span>
            )}
          </label>
        </>
      )}

      {draft.kind === 'sibling' && (
        <>
          <div className={ui.notice} role="note">
            {t('relations.siblingFallback')}
          </div>
          <label className={ui.field}>
            <span className={ui.label}>{t('relations.siblingBasis')}</span>
            <select
              className={ui.input}
              value={draft.sibling_basis ?? 'full'}
              onChange={(event) => set({ sibling_basis: event.target.value as SiblingBasis })}
            >
              {SIBLING_BASIS.map((basis) => (
                <option key={basis} value={basis}>
                  {t(`enums.sibling_basis.${basis}`)}
                </option>
              ))}
            </select>
          </label>
        </>
      )}

      <label className={ui.field}>
        <span className={ui.label}>{t('relations.note')}</span>
        <input
          className={ui.input}
          type="text"
          maxLength={2000}
          value={draft.note}
          onChange={(event) => set({ note: event.target.value })}
        />
      </label>

      <label className={ui.field}>
        <span className={ui.label}>{t('relations.sourceNote')}</span>
        <input
          className={ui.input}
          type="text"
          maxLength={500}
          value={draft.source_note}
          onChange={(event) => set({ source_note: event.target.value })}
        />
      </label>
    </>
  );
}
